import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { isNoop, useBrainApi } from "../../api/brainApi";
import { BrainRun, BrainRunStatus } from "../../api/types";
import { PageTitle, Panel, AlertBanner, btnSecondary } from "../../components/controlPlaneUi";
import Modal from "../../components/Modal";

// "45s" / "3m 12s" / "1h 4m"
function formatDuration(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rs = s % 60;
  if (m < 60) return `${m}m ${rs}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

const RUN_DOT: Record<BrainRunStatus, string> = {
  Running: "bg-[#C9A260] animate-pulse",
  Succeeded: "bg-emerald-500",
  Failed: "bg-red-500",
  Cancelled: "bg-[#5A5448]",
};
const RUN_TEXT: Record<BrainRunStatus, string> = {
  Running: "text-[#C9A260]",
  Succeeded: "text-emerald-400",
  Failed: "text-red-400",
  Cancelled: "text-[#9D9585]",
};

const RunStatusBadge: React.FC<{ status: BrainRunStatus }> = ({ status }) => (
  <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${RUN_TEXT[status]}`}>
    <span className={`h-2 w-2 rounded-full ${RUN_DOT[status]}`} />
    {status}
  </span>
);

// Engine workflow → human label.
const WORKFLOW_LABEL: Record<string, string> = {
  sync: "transcripts",
  source: "code + docs",
};

// Alert icon that reveals the full error in a styled tooltip on hover/focus (keeps the table tidy).
const ErrorTooltip: React.FC<{ message: string }> = ({ message }) => (
  <span className="relative inline-flex group/err">
    <button
      type="button"
      aria-label="Show error details"
      className="text-red-500 hover:text-red-600 rounded-full focus:outline-none focus-visible:ring-1 focus-visible:ring-red-400"
    >
      <svg
        className="h-3.5 w-3.5"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
      >
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="8" x2="12" y2="12" />
        <line x1="12" y1="16" x2="12.01" y2="16" />
      </svg>
    </button>
    <span
      role="tooltip"
      className="absolute left-0 top-full z-20 mt-1 hidden w-80 max-w-[28rem] max-h-48 overflow-y-auto whitespace-pre-wrap break-words rounded-sm border border-[#262420] bg-[#1a1917] p-2 text-[11px] leading-snug text-[#E5E0D5] shadow-lg group-hover/err:block group-focus-within/err:block"
    >
      {message}
    </span>
  </span>
);

const RunsPage: React.FC = () => {
  const api = useBrainApi();
  const [runs, setRuns] = useState<BrainRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const [noopMessage, setNoopMessage] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setRuns(await api.getRuns());
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "failed to load runs");
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    void load();
  }, [load]);

  // Live elapsed ticker.
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  // Poll while any run is in flight so status/elapsed stay fresh.
  const anyRunning = runs.some((r) => r.status === "Running");
  useEffect(() => {
    if (!anyRunning) return;
    const t = setInterval(() => void load(), 3000);
    return () => clearInterval(t);
  }, [anyRunning, load]);

  const cancel = async (run: BrainRun) => {
    setBusyId(run.id);
    setError(null);
    setNotice(null);
    try {
      await api.cancelRun(run.id);
      setNotice(`Run #${run.id} cancelled.`);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "cancel failed");
    } finally {
      setBusyId(null);
    }
  };

  const retry = async (run: BrainRun) => {
    setBusyId(run.id);
    setError(null);
    setNotice(null);
    try {
      // Retry only this run's workflow — sync and source fail independently.
      const res = await api.triggerRun(run.tenant, run.workflow);
      if (isNoop(res)) {
        setNoopMessage(res.reason); // nothing to do — explain, don't create a job
        return;
      }
      const stage = WORKFLOW_LABEL[run.workflow] ?? run.workflow;
      setNotice(`Re-ran ${stage} for "${run.tenant}" as run #${res.id}.`);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "retry failed");
    } finally {
      setBusyId(null);
    }
  };

  const duration = (run: BrainRun): string => {
    const started = new Date(run.startedAt).getTime();
    if (run.status === "Running") return formatDuration(now - started);
    if (run.finishedAt) return formatDuration(new Date(run.finishedAt).getTime() - started);
    return "—";
  };

  const fileSummary = (run: BrainRun): string => {
    const parts: string[] = [];
    if (run.filesCreated.length) parts.push(`+${run.filesCreated.length}`);
    if (run.filesModified.length) parts.push(`~${run.filesModified.length}`);
    if (run.filesDeleted.length) parts.push(`-${run.filesDeleted.length}`);
    return parts.length ? parts.join(" ") : "—";
  };

  // Group the paired sync + source runs from a single trigger (shared runGroupId); runs without
  // a group (legacy / scheduled) are their own singleton. Newest-first order is preserved.
  // Within a group, order the stages (sync → source) and label them off one base number: the
  // group's lowest run id, shown as "<base>a" (sync) and "<base>b" (source).
  const groups = useMemo(() => {
    const stageOrder = (w: string) => (w === "sync" ? 0 : w === "source" ? 1 : 2);
    const order: string[] = [];
    const map = new Map<string, BrainRun[]>();
    for (const r of runs) {
      const key = r.runGroupId || `solo-${r.id}`;
      if (!map.has(key)) {
        map.set(key, []);
        order.push(key);
      }
      map.get(key)!.push(r);
    }
    return order.map((key) => {
      const groupRuns = map.get(key)!.slice().sort((a, b) => stageOrder(a.workflow) - stageOrder(b.workflow));
      const base = Math.min(...groupRuns.map((r) => r.id));
      return { key, runs: groupRuns, base, paired: groupRuns.length > 1 };
    });
  }, [runs]);

  // "#5a" / "#5b" for the two stages of a paired ingestion; plain "#5" for a solo run.
  const runLabel = (base: number, paired: boolean, runId: number, index: number) =>
    paired ? `#${base}${String.fromCharCode(97 + index)}` : `#${runId}`;

  // Jobs with a stage still running — surfaced at the top with a stage-by-stage progress view.
  const inFlight = groups.filter((g) => g.runs.some((r) => r.status === "Running"));

  return (
    <div>
      <PageTitle kind="Jobs" name="jobs" />
      {error && <AlertBanner variant="error">{error}</AlertBanner>}
      {notice && <AlertBanner variant="ok">{notice}</AlertBanner>}
      {noopMessage && (
        <Modal title="Nothing to run" onClose={() => setNoopMessage(null)}>
          {noopMessage}
        </Modal>
      )}

      {inFlight.length > 0 && (
        <Panel title={`In flight (${inFlight.length})`} className="mb-4">
          <div className="space-y-3">
            {inFlight.map((g) => (
              <div key={g.key} className="border border-[#262420] p-3">
                <div className="flex items-center justify-between gap-2 mb-2">
                  <div className="text-sm font-medium text-[#E5E0D5]">
                    Domain ingestion #{g.base} · <span className="font-mono">{g.runs[0].tenant}</span>
                  </div>
                  <div className="text-[11px] text-[#5A5448] tabular-nums">
                    {formatDuration(now - new Date(g.runs[0].startedAt).getTime())} elapsed
                  </div>
                </div>
                {/* Stage-by-stage process: transcripts → code & docs */}
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  {g.runs.map((run, i) => (
                    <Fragment key={run.id}>
                      {i > 0 && <span className="text-[#5A5448]">→</span>}
                      <span className="inline-flex items-center gap-1.5">
                        <span className={`h-2 w-2 rounded-full ${RUN_DOT[run.status]}`} />
                        <span className="text-xs text-[#9D9585]">
                          {WORKFLOW_LABEL[run.workflow] ?? run.workflow}
                        </span>
                        <span className={`text-[11px] ${RUN_TEXT[run.status]}`}>
                          {run.status === "Running"
                            ? `running ${duration(run)}`
                            : `${run.status} · ${duration(run)}`}
                        </span>
                      </span>
                    </Fragment>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Panel>
      )}

      <Panel title={`Jobs (${runs.length})`}>
        {loading ? (
          <p className="text-sm text-slate-500">Loading…</p>
        ) : runs.length === 0 ? (
          <p className="text-sm text-slate-500">
            No jobs yet — start one from the <span className="font-mono">Upload</span> page.
          </p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-[#5A5448] border-b border-[#262420]">
                <th className="py-1.5 pr-2 font-medium">Run</th>
                <th className="py-1.5 pr-2 font-medium">Domain</th>
                <th className="py-1.5 pr-2 font-medium">Workflow</th>
                <th className="py-1.5 pr-2 font-medium">Trigger</th>
                <th className="py-1.5 pr-2 font-medium">Status</th>
                <th className="py-1.5 pr-2 font-medium">Triggered</th>
                <th className="py-1.5 pr-2 font-medium">Duration</th>
                <th className="py-1.5 pr-2 font-medium">Files</th>
                <th className="py-1.5 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {groups.map((g) => {
                const paired = g.paired;
                return (
                  <Fragment key={g.key}>
                    {paired && (
                      <tr className="bg-[#141210]">
                        <td
                          colSpan={9}
                          className="py-1.5 px-2 text-[11px] font-medium text-[#5A5448] border-b border-[#1f1e1b]"
                        >
                          Domain ingestion #{g.base} · <span className="font-mono">{g.runs[0].tenant}</span> ·{" "}
                          {new Date(g.runs[0].startedAt).toLocaleString()}
                        </td>
                      </tr>
                    )}
                    {g.runs.map((run, i) => (
                      <tr key={run.id} className="border-b border-[#1f1e1b] align-top">
                        <td className={`py-2 pr-2 font-mono ${paired ? "border-l-2 border-[#C9A260]/30 pl-2" : ""}`}>
                          {runLabel(g.base, paired, run.id, i)}
                        </td>
                        <td className="py-2 pr-2 font-mono">{run.tenant}</td>
                        <td className="py-2 pr-2 text-[#9D9585]">
                          {WORKFLOW_LABEL[run.workflow] ?? run.workflow}
                        </td>
                        <td className="py-2 pr-2 text-[#9D9585]">{run.trigger}</td>
                        <td className="py-2 pr-2">
                          <span className="inline-flex items-center gap-1.5">
                            <RunStatusBadge status={run.status} />
                            {run.status === "Failed" && run.error && (
                              <ErrorTooltip message={run.error} />
                            )}
                          </span>
                        </td>
                        <td className="py-2 pr-2 text-slate-500 whitespace-nowrap">
                          {new Date(run.startedAt).toLocaleString()}
                        </td>
                        <td className="py-2 pr-2 tabular-nums text-slate-600">{duration(run)}</td>
                        <td className="py-2 pr-2 font-mono text-slate-600" title={[...run.filesCreated, ...run.filesModified].join("\n") || undefined}>
                          {fileSummary(run)}
                        </td>
                        <td className="py-2 text-right">
                          {run.status === "Running" ? (
                            <button
                              type="button"
                              className={btnSecondary}
                              disabled={busyId === run.id}
                              onClick={() => cancel(run)}
                            >
                              {busyId === run.id ? "Cancelling…" : "Cancel"}
                            </button>
                          ) : run.status === "Failed" || run.status === "Cancelled" ? (
                            <button
                              type="button"
                              className={btnSecondary}
                              disabled={busyId === run.id}
                              onClick={() => retry(run)}
                              title={`Re-run just the ${WORKFLOW_LABEL[run.workflow] ?? run.workflow} stage for "${run.tenant}"`}
                            >
                              {busyId === run.id ? "Retrying…" : "Retry"}
                            </button>
                          ) : null}
                        </td>
                      </tr>
                    ))}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </Panel>
    </div>
  );
};

export default RunsPage;
